import { Add, ArrowBackIos, Edit, EditNotifications, LockOpen, LockOutlined, Save } from '@mui/icons-material';
import { Box, Button, Grid, LinearProgress, Link, SxProps, Theme, Toolbar, Typography } from '@mui/material';
import { useUploadQuoteFiles } from 'shared/mutations/pdf-upload-mutation';
import { CustomerInformation } from 'quote-detail/customer-information';
import { NewProduct } from 'quote-detail/new-product';
import { PdfDialog } from 'quote-detail/pdf-dialog';
import { ProductDetails } from 'quote-detail/product-details';
import { QuoteDetailUpdateTotal } from 'quote-detail/quote-detail-update-total';
import { SubmitConfirmation } from 'quote-detail/submit-confirmation';
import { useCallback, useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'routing/routes';
import { BRANDS, STATUS } from 'shared/constants';
import { useQuoteState } from 'shared/contexts';
import { FormTextField } from 'shared/controls';
import { useMultiKeyPress, useTitle } from 'shared/hooks';
import { useSendEmail, useUpdateQuote } from 'shared/mutations';
import { useDeletePdfFile } from 'shared/mutations/pdf-delete-mutation';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useUsers } from 'shared/queries';
import {
  Brands,
  Customer,
  ProductDetailMetric,
  Quote,
  QuoteDetailsForm,
  IQuoteDocumentation,
  UserInfo,
  ProductLookup,
} from 'shared/types';
import { addPdfExtension, buildCsvExport, formatDate, hashFileName, titleCase } from 'shared/utility';
import { ExpenseDetails } from './expense-details';
import { NewExpense } from './new-expense';
import { QuoteDocumentation } from './quote-documentation';
import { OutcomeDropdown } from 'shared/components/outcome-dropdown';
import { useService } from 'shared/providers';
import { LookupRecord } from '@price-for-profit/micro-services';
import { ReminderDialog } from './reminder-dialog';
import { CustomProductContainer } from './custom-product-container';
import { NewCustomProductDialog } from './new-custom-product-dialog';
import { OrderNotesDetails } from './order-notes-details';
import { NewOrderNote } from './new-order-note';
import { ProductRecommendationsContainer } from './product-recommendations-container';
import { NewProductByRecommendation } from './new-product-by-recommendation';

const rootStyles: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.background.paper,
  width: '100%',
  '& h5': {
    marginBottom: '15px',
    display: 'flex',
    '@media screen and (max-width: 479px)': {
      flexDirection: 'column',
    },
  },
});
const backContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.background.default,
});
const backStyles: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  marginRight: theme.spacing(2),
});
const loadingBarStyles: SxProps<Theme> = (theme: Theme) => ({
  height: 4,
  backgroundColor: theme.palette.primary.contrastText,
});
const headerStyles: SxProps<Theme> = (theme: Theme) => ({
  padding: theme.spacing(2.5),
  paddingLeft: theme.spacing(5),
  backgroundColor: theme.palette.primary.contrastText,
  justifyContent: 'space-between',
  '@media screen and (max-width: 479px)': {
    flexDirection: 'column',
  },
});
const headerButtonsStyles: SxProps<Theme> = {
  width: 'fit-content',
};
const lockContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(2),
});
const lockOpenStyles: SxProps<Theme> = (theme: Theme) => ({
  color: 'green',
  marginRight: theme.spacing(0.5),
});
const lockOutlinedStyles: SxProps<Theme> = (theme: Theme) => ({
  marginRight: theme.spacing(0.5),
});
const exportButtonStyles: SxProps<Theme> = {
  textDecoration: 'none',
};
const infoContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  padding: theme.spacing(5),
});
const detailsContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
});
const quoteNumberInputStyle: SxProps<Theme> = {
  flex: '25%',
  '& .MuiInputBase-input': {
    padding: '5px 25px',
  },
};

interface QuoteDetailProps {
  brand: Brands;
  id: string;
  user: UserInfo;
  quote: Quote;
  isLoading: boolean;
}

export function QuoteDetail({ brand, id, user, quote, isLoading }: QuoteDetailProps) {
  useTitle(`${BRANDS[brand]} - Quote Detail - ${id}`);
  const { quoteService } = useService();
  const history = useHistory();

  const {
    editMode,
    toggleEditMode,
    toggleConfirmation,
    togglePdfDialog,
    pdfDialogOpen,
    reminderDialogOpen,
    reminderAllowed,
    setTempProduct,
    setReminderAllowed,
    toggleNewProductDialog,
    setTempExpense,
    setTempOrderNote,
    toggleNewExpenseDialog,
    toggleNewOrderNoteDialog,
    toggleReminderDialogOpen,
  } = useQuoteState();

  const { users } = useUsers(brand, user.id);
  const shouldResetToDraft = useMultiKeyPress();
  const updateMutation = useUpdateQuote();
  const assignedSalesRepsEmail = quote.customer.assignedSalesRepsEmail;
  const customerId = quote.customer.customerNumber;
  const userEmail = users?.find(
    user => `${user.firstName} ${user.lastName}`.toLowerCase() === quote.salesRep.toLowerCase()
  )?.email;
  const emailToSend = quote && (assignedSalesRepsEmail?.toLowerCase() || userEmail);
  const emailMutation = useSendEmail();
  const isCimtec: boolean = brand === 'cimtec';

  const methods = useForm<QuoteDetailsForm>({
    defaultValues: { ...quote, files: undefined }, // files field needs to be undefined but we got an empty array from server
  });
  const { handleSubmit, formState, reset } = methods;
  const { isDirty, errors } = formState;
  const deletePdfMutation = useDeletePdfFile();

  const setQuote = useCallback(
    (quote: Quote) => {
      reset({ ...quote, files: undefined });
    },
    [reset]
  );

  const resetQuoteToDraft = () => {
    const updatedQuote: Quote = {
      ...quote,
      status: STATUS.draft,
      lastRevisionDate: new Date().toISOString(),
    };
    updateMutation.mutate(updatedQuote);
  };

  const editQuoteByClick = () => {
    if (quote?.status === STATUS.submitted) {
      resetQuoteToDraft();
    }
  };

  useEffect(() => {
    if (quote?.status === STATUS.submitted && shouldResetToDraft) {
      resetQuoteToDraft();
    }
    // eslint-disable-next-line
  }, [shouldResetToDraft]);

  useEffect(() => {
    if (quote) {
      if (userEmail) {
        if (
          new Date(quote.expirationDate) < new Date() ||
          (quote.outcome && (quote.outcome === 'WON' || quote.outcome === 'LOST'))
        ) {
          setReminderAllowed(false);
        } else {
          setReminderAllowed(true);
        }
      } else {
        setReminderAllowed(false);
      }
      setQuote(quote);
    }
  }, [setQuote, quote, setReminderAllowed, userEmail]);

  const { csvData, csvHeaders } = buildCsvExport(quote, user);
  const uploadPdfMutation = useUploadQuoteFiles();

  const toQuoteList = useCallback(() => {
    history.push(ROUTES.getSite(brand));
  }, [history, brand]);

  const uploadFiles = async (data: QuoteDetailsForm, index: number, id: string) => {
    const nowISOString = new Date().toISOString(); // we declare this variable multiple times cuz we need the exact time for each function
    const originalDocumentName = data.files?.[index].name || '';
    const fileId = hashFileName(`${id}${originalDocumentName}${nowISOString}`);
    const formData = new FormData();
    const fileToUpload = data.files ? [data.files[index]] : [];
    formData.append('file', new File(fileToUpload, addPdfExtension(fileId)));

    await uploadPdfMutation.mutateAsync(formData);
  };

  const onSave = async (modified: QuoteDetailsForm) => {
    if (modified.products?.length > 0 && modified.products.every(product => product.recalculate)) {
      // setError('products', {
      //   message: 'There are products that need to be recalculated',
      //   type: 'custom',
      // });
      return;
    }

    let updatedQuoteDocumentation: IQuoteDocumentation[] = quote.quoteDocumentation
      ? [...quote.quoteDocumentation]
      : [];

    if (!!modified.quoteFilesToDelete && modified.quoteFilesToDelete.length > 0) {
      modified.quoteFilesToDelete.forEach(async file => {
        await deletePdfMutation.mutateAsync(addPdfExtension(file.id));
      });
      const quoteIdsMarkedForDeletion = modified.quoteFilesToDelete.map(q => q.id);
      updatedQuoteDocumentation = updatedQuoteDocumentation.filter(q => !quoteIdsMarkedForDeletion.includes(q.id));
    }

    const nowISOString = new Date().toISOString();
    if (modified?.files) {
      for (let i = 0; i < modified.files.length; i++) {
        uploadFiles(modified, i, modified.id);
        updatedQuoteDocumentation = [
          ...updatedQuoteDocumentation,
          {
            id: hashFileName(`${modified.id}${modified.files?.[i].name || ''}${nowISOString}`),
            documentName: modified.files?.[i].name || '',
            user: user.displayName,
            uploadDate: nowISOString,
          },
        ];
      }
    }

    modified.pdfInfo.modifiedQuoteNumber = modified.quoteNumber;

    if (modified.outcome && (modified.outcome === 'WON' || modified.outcome === 'LOST')) {
      modified.reminderDate = undefined;
    }

    const updatedQuote: QuoteDetailsForm = {
      ...quote,
      ...modified,
      quoteFilesToDelete: [],
      products: [...(modified.products || [])],
      expenses: [...(modified.expenses || [])],
      customProducts: [...(modified.customProducts || [])],
      orderNotes: [...(modified.orderNotes || [])],
      quoteDocumentation: updatedQuoteDocumentation,
      lastRevisionDate: nowISOString,
    };

    await updateMutation.mutateAsync(updatedQuote);
    toggleEditMode();
  };

  const submitQuote = async () => {
    const nowISOString = new Date().toISOString();
    if (quote) {
      if (isCimtec) {
        quote['oe_hdr.job_name'] = quote.quoteNumber;
      }
      const updatedQuote: Quote = {
        ...quote,
        status: STATUS.submitted,
        lastRevisionDate: nowISOString,
        submitDate: nowISOString,
      };
      await updateMutation.mutateAsync(updatedQuote);
    }
  };

  const cancelChanges = () => {
    setQuote(quote);
    toggleEditMode();
  };

  const openPdfDialog = () => {
    togglePdfDialog();
  };
  const sendEmail = async () => {
    if (quote && emailToSend) {
      await emailMutation.mutateAsync([quote, emailToSend]);
    }
  };

  const handleSetReminder = () => {
    toggleReminderDialogOpen();
  };

  const handleCopyQuote = async (): Promise<void> => {
    let updatedProducts: LookupRecord<ProductLookup>[] = [];

    await Promise.all(
      quote.products.map(async p => {
        const updatedProduct = await quoteService.getProduct(brand, p.vendorId, p.itemId);
        if (!!updatedProduct) {
          updatedProducts.push(updatedProduct);
        }
      })
    );

    history.push(`/quotes/${brand}/new`, {
      state: {
        quoteToCopy: quote,
        productsToCopy: updatedProducts,
      },
    });
  };

  const metrics: ProductDetailMetric[] = [
    { label: 'Created By', value: quote?.salesRep ? titleCase(quote.salesRep) : '' },
    { label: 'Assigned Sales Rep', value: quote?.assignedSalesRep ? titleCase(quote.assignedSalesRep) : '' },
    { label: 'Create Date', value: quote?.createdDate ? formatDate(new Date(quote.createdDate)) : '' },
  ];

  if (userEmail) {
    metrics.push({
      label: 'Reminder set for',
      value: quote?.reminderDate ? formatDate(new Date(quote.reminderDate)) : 'N/A',
    });
  }

  const saveButtonText = updateMutation.isLoading ? 'saving...' : 'save changes';
  const disableSave = !isDirty || updateMutation.isLoading;

  return (
    <FormProvider {...methods}>
      <QuoteDetailUpdateTotal />
      <Grid sx={backContainerStyles} container justifyContent='flex-end'>
        <Link
          sx={backStyles}
          component='button'
          variant='subtitle1'
          underline='none'
          color='textSecondary'
          onClick={toQuoteList}
        >
          <ArrowBackIos fontSize='inherit' />
          Back To Quote List
        </Link>
      </Grid>
      <Box sx={loadingBarStyles}>{isLoading && <LinearProgress />}</Box>
      <form aria-label='form' id='quote-detail-form' onSubmit={handleSubmit(onSave)}>
        <Box sx={rootStyles}>
          <Toolbar sx={headerStyles}>
            <Grid>
              <Grid>
                <Typography variant='h5' style={{ flexDirection: 'column' }}>
                  <Box fontWeight='bold' display='inline'>
                    {`${quote?.customer.name}`}
                    &nbsp;
                  </Box>
                  {quote?.status === STATUS.draft && editMode ? (
                    <FormTextField
                      defaultValue={quote?.quoteNumber}
                      label='Quote'
                      name='quoteNumber'
                      rules={{
                        required: {
                          message: 'Quote number is required',
                          value: true,
                        },
                      }}
                      error={Boolean(errors?.quoteNumber)}
                      helperText={errors?.quoteNumber?.message}
                      sx={quoteNumberInputStyle}
                    />
                  ) : (
                    <span>{`Quote #: ${quote?.quoteNumber}`}</span>
                  )}
                </Typography>
              </Grid>
              <Grid>
                {quote?.status === STATUS.draft && editMode ? (
                  <OutcomeDropdown />
                ) : quote.outcome ? (
                  <Typography variant='subtitle1'>
                    <Box fontWeight='bold' display='inline'>
                      Quote Outcome: &nbsp;
                    </Box>
                    {quote?.outcome}
                  </Typography>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
            <Grid spacing={2} container sx={headerButtonsStyles}>
              {quote?.status === STATUS.draft && (
                <>
                  <Grid item sx={lockContainerStyles}>
                    <LockOpen sx={lockOpenStyles} />
                    <Typography>{STATUS.draft}</Typography>
                  </Grid>

                  <Grid item>
                    <Button
                      variant='outlined'
                      color='secondary'
                      onClick={handleCopyQuote}
                      startIcon={<ContentCopyIcon />}
                    >
                      Copy quote
                    </Button>
                  </Grid>

                  {editMode ? (
                    <>
                      <Grid item>
                        <Button variant='contained' onClick={cancelChanges}>
                          cancel
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          color='primary'
                          variant='contained'
                          type='submit'
                          form='quote-detail-form'
                          disabled={disableSave}
                        >
                          {saveButtonText}
                        </Button>
                      </Grid>
                    </>
                  ) : (
                    <>
                      {reminderAllowed ? (
                        <Grid item>
                          <Button
                            color='primary'
                            variant='contained'
                            onClick={handleSetReminder}
                            startIcon={<EditNotifications />}
                          >
                            {quote.reminderDate ? 'Change' : 'Set'} Reminder
                          </Button>
                        </Grid>
                      ) : (
                        <></>
                      )}
                      <Grid item>
                        <Button color='secondary' variant='contained' onClick={toggleEditMode} startIcon={<Edit />}>
                          edit quote
                        </Button>
                      </Grid>
                    </>
                  )}

                  <Grid item>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={toggleConfirmation}
                      disabled={editMode}
                      startIcon={<Save />}
                    >
                      submit quote
                    </Button>
                  </Grid>
                </>
              )}
              {quote?.status === STATUS.submitted && (
                <>
                  <Grid item sx={lockContainerStyles}>
                    <LockOutlined sx={lockOutlinedStyles} color='disabled' />
                    <Typography>{STATUS.submitted}</Typography>
                  </Grid>
                  <Grid item>
                    <Button variant='contained' color='primary' onClick={editQuoteByClick}>
                      unlock quote
                    </Button>
                  </Grid>
                  <Grid item>
                    <Box sx={exportButtonStyles}>
                      <CSVLink headers={csvHeaders} data={csvData} filename={`${quote?.quoteNumber}-export.csv`}>
                        <Button variant='contained' color='primary'>
                          export erp quote
                        </Button>
                      </CSVLink>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Button variant='contained' color='primary' onClick={openPdfDialog}>
                      export customer quote
                    </Button>
                  </Grid>
                  {emailToSend && (
                    <Grid item>
                      <Button variant='contained' color='primary' onClick={sendEmail}>
                        send email notification
                      </Button>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Toolbar>
          <Grid sx={infoContainerStyles}>
            <CustomerInformation
              customer={quote?.customer || ({} as Customer)}
              metrics={metrics}
              quote={quote}
              editMode={editMode}
            />
            <Grid sx={detailsContainerStyles}>
              <Grid container direction='row' justifyContent='space-between'>
                <Typography variant='h5'>Product Details</Typography>
                {editMode && (
                  <Grid item>
                    <Button
                      color='secondary'
                      variant='outlined'
                      startIcon={<Add fontSize='large' />}
                      onClick={() => {
                        setTempProduct(null);
                        toggleNewProductDialog();
                      }}
                    >
                      add product
                    </Button>
                  </Grid>
                )}
              </Grid>
              <ProductDetails />
            </Grid>
            <ProductRecommendationsContainer styles={detailsContainerStyles} />
            <CustomProductContainer styles={detailsContainerStyles} />
            <Grid sx={detailsContainerStyles}>
              <Grid container direction='row' justifyContent='space-between'>
                <Typography variant='h5'>Expense Details</Typography>
                {editMode && (
                  <Grid item>
                    <Button
                      color='secondary'
                      variant='outlined'
                      onClick={() => {
                        setTempExpense(null);
                        toggleNewExpenseDialog();
                      }}
                    >
                      add expense
                    </Button>
                  </Grid>
                )}
              </Grid>
              <ExpenseDetails />
            </Grid>
            <Grid sx={detailsContainerStyles}>
              <Grid container direction='row' justifyContent='space-between'>
                <Typography variant='h5'>PDFs attached</Typography>
                <Grid item>
                  <QuoteDocumentation quote={quote} disabled={!editMode} />
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={detailsContainerStyles}>
              <Grid container direction='row' justifyContent='space-between'>
                <Typography variant='h5'>Order notes</Typography>
                {editMode && (
                  <Grid item>
                    <Button
                      color='secondary'
                      variant='outlined'
                      onClick={() => {
                        setTempOrderNote(null);
                        toggleNewOrderNoteDialog();
                      }}
                    >
                      add order notes
                    </Button>
                  </Grid>
                )}
                <OrderNotesDetails />
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </form>
      <NewProduct customerId={customerId} />
      <NewProductByRecommendation customerId={customerId} />
      <NewCustomProductDialog />
      <NewExpense />
      <NewOrderNote userName={user.displayName} />
      <SubmitConfirmation onOk={submitQuote} />
      {quote && pdfDialogOpen && <PdfDialog quote={quote} />}
      {quote && reminderDialogOpen && <ReminderDialog quote={quote} />}
    </FormProvider>
  );
}
